import axiosInstance from './axiosInstance';
import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AdvanceFilter, AdvanceFilterOperator } from '@/types/types';
import type { HIWorkOrderCreatePayload, HIWorkOrderUpdatePayload } from '@/pages/homeInspection/hi.schema';
import type { HomeInspectionWorkOrderResponse } from '@/types/homeInspection.types';
import type {
  AssignTeamUserRequest,
  HIAddCommentResponse,
  HIInvoiceEditRequest,
  HIInvoiceEmailShareResponse,
  HIInvoiceResponse,
  HIInvoiceUpdateResponse,
  HIWorkOrderCommentsResponse,
  HIWorkOrderListParams,
  HIWorkOrderListResponse,
  HIWorkOrderUpdateStatusRequest,
  HomeInspectionWorkOrderStatus,
} from '@/types/homeInspection.types';
import { normalizeAxiosError } from '@/lib/error';

const isoToMMDDYYYY = (iso: string): string => {
  const date = new Date(iso);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${month}${day}${year}`;
};

export const hiFiltersToParams = (filters: AdvanceFilter[]): HIWorkOrderListParams => {
  const params: HIWorkOrderListParams = {};

  for (const f of filters) {
    if (!f.field || !f.value) continue;

    switch (f.field) {
      case 'createdOn':
        if (f.operator === AdvanceFilterOperator.EQUALS) {
          params.from = isoToMMDDYYYY(f.value);
          params.to = isoToMMDDYYYY(f.value);
        } else if (
          f.operator === AdvanceFilterOperator.GREATER_THAN ||
          f.operator === AdvanceFilterOperator.GREATER_THAN_OR_EQUAL_TO
        ) {
          params.from = isoToMMDDYYYY(f.value);
        } else if (
          f.operator === AdvanceFilterOperator.LESS_THAN ||
          f.operator === AdvanceFilterOperator.LESS_THAN_OR_EQUAL_TO
        ) {
          params.to = isoToMMDDYYYY(f.value);
        }
        break;
      case 'lastServeDate':
        if (f.operator === AdvanceFilterOperator.EQUALS) {
          params.lastServeDateFrom = isoToMMDDYYYY(f.value);
          params.lastServeDateTo = isoToMMDDYYYY(f.value);
        } else if (
          f.operator === AdvanceFilterOperator.GREATER_THAN ||
          f.operator === AdvanceFilterOperator.GREATER_THAN_OR_EQUAL_TO
        ) {
          params.lastServeDateFrom = isoToMMDDYYYY(f.value);
        } else if (
          f.operator === AdvanceFilterOperator.LESS_THAN ||
          f.operator === AdvanceFilterOperator.LESS_THAN_OR_EQUAL_TO
        ) {
          params.lastServeDateTo = isoToMMDDYYYY(f.value);
        }
        break;
      case 'clientId':
        params.clientId = f.value.trim();
        break;
      case 'caseNumber':
        params.caseNumber = f.value;
        break;
      case 'status':
        params.status = f.value as HomeInspectionWorkOrderStatus;
        break;
      case 'teamId':
        params.teamId = f.value; // 'MY_TEAMS' passed as-is — same as PEMaintenance
        break;
      case 'workOrderNumber':
        params.workOrderNumber = f.value;
        break;
      case 'serverId':
        params.serverId = f.value;
        break;
      case 'caseName':
        params.caseName = f.value;
        break;
      case 'fileNumber':
        params.fileNumber = f.value;
        break;
      case 'clientContact':
        params.contactName = f.value;
        break;
      case 'mortgageCompany':
        params.mortgageCompanyOrPlaintiff = f.value;
        break;
      case 'propertyAddress':
        params.propertyAddress = f.value;
        break;
      case 'city':
        params.city = f.value;
        break;
      case 'state':
        params.state = f.value;
        break;
      case 'zipCode':
        params.zipCode = f.value;
        break;
      case 'ownerOrDefendantName':
        params.ownerOrDefendantName = f.value;
        break;
    }
  }

  return params;
};

export const getHIWorkOrders = async (
  params: HIWorkOrderListParams
): Promise<{ data?: HIWorkOrderListResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.get<HIWorkOrderListResponse>('/home-inspection/work-orders/', { params });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const useCreateHIWorkOrder = () => {
  return useMutation<HomeInspectionWorkOrderResponse, unknown, HIWorkOrderCreatePayload>({
    mutationFn: async (payload) => {
      const response = await axiosInstance.post<HomeInspectionWorkOrderResponse>(
        '/home-inspection/work-orders/',
        payload
      );
      return response.data;
    },
  });
};

export const useHIWorkOrders = (
  params: HIWorkOrderListParams,
  options?: { query?: UseQueryOptions<HIWorkOrderListResponse> }
) => {
  const queryOptions = options?.query;
  const queryKey = queryOptions?.queryKey
    ? ['hi-work-orders', params, queryOptions.queryKey]
    : ['hi-work-orders', params];

  return useQuery<HIWorkOrderListResponse>({
    ...queryOptions,
    queryKey,
    queryFn: async () => {
      const { data, error } = await getHIWorkOrders(params);
      if (error) throw error;
      return data!;
    },
  });
};

export const getHIWorkOrderDetail = async (
  id: string
): Promise<{ data?: HomeInspectionWorkOrderResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.post<HomeInspectionWorkOrderResponse>(
      `/home-inspection/work-orders/${id}/fetch-for-edit`
    );
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const useHIWorkOrderDetail = (id: string | null) => {
  return useQuery<HomeInspectionWorkOrderResponse>({
    queryKey: ['hi-work-order-detail', id],
    queryFn: async () => {
      const { data, error } = await getHIWorkOrderDetail(id!);
      if (error) throw error;
      return data!;
    },
    enabled: id !== null,
  });
};

export const useHIWorkOrderByNumber = (workOrderNumber: string | null) => {
  return useQuery<HomeInspectionWorkOrderResponse>({
    queryKey: ['hi-work-order-by-number', workOrderNumber],
    queryFn: async () => {
      const { data, error } = await getHIWorkOrders({ workOrderNumber: workOrderNumber!, pageSize: 50 });
      if (error) throw error;
      const workOrder = data?.workOrders[0];
      if (!workOrder) throw new Error('Work order not found');
      return workOrder;
    },
    enabled: workOrderNumber !== null,
  });
};

export const getHIInvoice = async (
  workOrderId: string
): Promise<{ data?: HIInvoiceResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.get<HIInvoiceResponse>(
      `/home-inspection/work-orders/${workOrderId}/invoice`
    );
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const useHIInvoice = (workOrderId: string | null) => {
  return useQuery<HIInvoiceResponse>({
    queryKey: ['hi-invoice', workOrderId],
    queryFn: async () => {
      const { data, error } = await getHIInvoice(workOrderId!);
      if (error) throw error;
      if (!data) {
        throw new AxiosError(
          'Invoice not found',
          AxiosError.ERR_BAD_RESPONSE,
          undefined,
          undefined,
          {
            status: 404,
            statusText: 'Not Found',
            data: { message: 'Invoice not found' },
            headers: {},
            config: {} as never,
          }
        );
      }
      return data;
    },
    enabled: Boolean(workOrderId),
  });
};

export const patchHIInvoice = async (
  invoiceId: string,
  body: HIInvoiceEditRequest
): Promise<{ data?: HIInvoiceUpdateResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.patch<HIInvoiceUpdateResponse>(
      `/home-inspection/invoice/${encodeURIComponent(invoiceId)}`,
      body
    );
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const usePatchHIInvoiceMutation = (
  options?: UseMutationOptions<
    HIInvoiceUpdateResponse,
    unknown,
    { invoiceId: string; body: HIInvoiceEditRequest; workOrderId?: string | null }
  >
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};
  return useMutation({
    mutationFn: async ({ invoiceId, body }) => {
      const { data, error } = await patchHIInvoice(invoiceId, body);
      if (error) throw error;
      return data!;
    },
    ...rest,
    onSuccess: async (data, variables, context) => {
      if (variables.workOrderId) {
        await queryClient.invalidateQueries({ queryKey: ['hi-invoice', variables.workOrderId] });
      }
      await onSuccess?.(data, variables, context);
    },
  });
};

export const emailHIInvoice = async (
  invoiceId: string
): Promise<{ data?: HIInvoiceEmailShareResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.post<HIInvoiceEmailShareResponse>(
      `/home-inspection/invoice/${invoiceId}/email`,
      {}
    );
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const useEmailHIInvoiceMutation = (
  options?: UseMutationOptions<HIInvoiceEmailShareResponse, unknown, { invoiceId: string }>
) => {
  return useMutation({
    mutationFn: async ({ invoiceId }) => {
      const { data, error } = await emailHIInvoice(invoiceId);
      if (error) throw error;
      return data!;
    },
    ...options,
  });
};

export const getHIWorkOrderComments = async (
  id: string
): Promise<{ data?: HIWorkOrderCommentsResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.get<HIWorkOrderCommentsResponse>(
      `/home-inspection/work-orders/${id}/comments`
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useHIWorkOrderComments = (id: string | null) => {
  return useQuery<HIWorkOrderCommentsResponse>({
    queryKey: ['hi-work-order-comments', id],
    queryFn: async () => {
      const { data, error } = await getHIWorkOrderComments(id!);
      if (error) throw error;
      return data!;
    },
    enabled: id !== null,
  });
};

export const useFetchHIWorkOrderForEdit = () => {
  return useMutation<HomeInspectionWorkOrderResponse, unknown, string>({
    mutationFn: async (id) => {
      const response = await axiosInstance.post<HomeInspectionWorkOrderResponse>(
        `/home-inspection/work-orders/${id}/fetch-for-edit`
      );
      return response.data;
    },
  });
};

export const useUpdateHIWorkOrder = () => {
  return useMutation<HomeInspectionWorkOrderResponse, unknown, { id: string; payload: HIWorkOrderUpdatePayload }>({
    mutationFn: async ({ id, payload }) => {
      const response = await axiosInstance.patch<HomeInspectionWorkOrderResponse>(
        `/home-inspection/work-orders/${id}/update`,
        payload
      );
      return response.data;
    },
  });
};

export const assignHIServer = async (
  workOrderId: string,
  serverId: string
): Promise<HomeInspectionWorkOrderResponse> => {
  const response = await axiosInstance.patch<HomeInspectionWorkOrderResponse>(
    `/home-inspection/work-orders/${workOrderId}/assign-server`,
    { serverId }
  );
  return response.data;
};

export const useAssignHIServer = () => {
  return useMutation<HomeInspectionWorkOrderResponse, unknown, { id: string; serverId: string }>({
    mutationFn: async ({ id, serverId }) => assignHIServer(id, serverId),
  });
};

export const assignHomeInspectionWorkOrder = async (
  workOrderId: string,
  body: AssignTeamUserRequest
): Promise<HomeInspectionWorkOrderResponse> => {
  const response = await axiosInstance.patch<HomeInspectionWorkOrderResponse>(
    `/home-inspection/work-orders/${workOrderId}/assign-team-user`,
    body
  );
  return response.data;
};

export const useAssignHomeInspectionWorkOrder = () => {
  return useMutation<
    HomeInspectionWorkOrderResponse,
    unknown,
    { id: string; body: AssignTeamUserRequest }
  >({
    mutationFn: async ({ id, body }) => assignHomeInspectionWorkOrder(id, body),
  });
};

export const cancelHIWorkOrderEdit = async (id: string): Promise<void> => {
  await axiosInstance.post(`/home-inspection/work-orders/${id}/cancel-edit`);
};

export const useUpdateHIWorkOrderStatus = () => {
  return useMutation<HomeInspectionWorkOrderResponse, unknown, { id: string } & HIWorkOrderUpdateStatusRequest>({
    mutationFn: async ({ id, status }) => {
      const response = await axiosInstance.patch<HomeInspectionWorkOrderResponse>(
        `/home-inspection/work-orders/${id}/update-status`,
        { status }
      );
      return response.data;
    },
  });
};

export const addHIWorkOrderComment = async (
  id: string,
  commentText: string
): Promise<{ data?: HIAddCommentResponse; error?: unknown }> => {
  try {
    const response = await axiosInstance.post<HIAddCommentResponse>(`/home-inspection/work-orders/${id}/comments`, {
      commentText,
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const exportHIORPdf = async (
  workOrderId: string
): Promise<{
  data?: Blob;
  error?: unknown;
}> => {
  try {
    const response = await axiosInstance.post<Blob>(
      `/home-inspection/work-orders/${workOrderId}/export-HIOR-sheet`,
      {},
      { responseType: 'blob' }
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};
